import { makeAutoObservable } from "mobx";
import type React from "react";
import { loadObject } from "src/common/api/apiObject";
import { onError } from "src/common/onError";
import {
  SelectionSetings,
  TableLoadParams,
  TableStore,
} from "src/components/tables/TableStore";
import { walk } from "src/components/TreeStd";
import { hasPermissionIn, Permission } from "src/types/Permission";
import { TreeLikeData } from "src/types/TreeLikeData";
import { ZAttribute } from "src/types/ZAttribute";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZObjState } from "src/types/ZObjState";

import {
  availableFunctions,
  BsFuncDef,
} from "src/businessServices/availableFunctions";
import { loadAttrTypeMap } from "src/common/attributes";
import { ConfigManager } from "src/common/ConfigManager";
import { ZUid } from "src/types/zUid";
import { userPreferenceKeys } from "src/common/userPreferenceKeys";
import {
  loadObjectStates,
  makeObjectStatesMap,
} from "../ManagementPage/Obj2Tab/roles/rolesApi";

import {
  loadFilterSupportedAtts,
  loadObjectAttrinbutesAll,
  makeObjectAttributesMap,
} from "../ManagementPage/objectsApi";
import { AttsTreeStore } from "./AttsTree/AttsTreeStore";
import { loadTreeAttrValues } from "./AttsTree/loadTreeAttrValues";
import { attr2TreeData } from "./AttsTree/transforms";
import { EntityFilterItem, EntityFilterTreeData } from "./AttsTree/types";
import { CheckListWithOrderStore } from "./CheckListWithOrder/CheckListWithOrderStore";
import { DefaultTSettingsStore } from "./DefaultTSettingsStore";
import {
  EntFilterActionsMeta,
  EntFilterActionType,
} from "./EntityFiltersPage.types";
import {
  EntityFilterStateStrategy,
  getLatestEntityFilterState,
  ZEntityFilterStateConfig,
} from "./EntityFiltersStateStrategy";
import { withEntityFilterStateDataFilterRestriction } from "./filterRestriction/withEntityFilterStateDataFilterRestriction";
import { compoundEntityTableStore2 } from "./EntityList/compoundEntityTableStore2";
import {
  EntityFilterRestriction,
  EntityFilters,
  ZEntityRow,
} from "./EntityList/types";
import {
  EntityTableStrategy,
  getLatestEntityTSettings,
  ZEntityTSettings,
} from "./EntityTableStrategy";
import { EntityCardStore } from "../EntityCardPage/EntityCardStore";
import { sortFilterTreeValues } from "./sortFilterTreeValues";
import { ATVTemplateToken } from "./AsyncTableView";
import { makeFilterRestrictionNodeKey } from "./filterRestriction/makeFilterRestrictionNodeKey";
import { withFilterRestriction } from "./filterRestriction/withFilterRestriction";
import { reconcileEntityFilterState } from "./reconcileEntityFilterState";

const store2EntitySearchState = (
  store: EntityFiltersPageStore,
): ZEntityFilterStateConfig =>
  getLatestEntityFilterState({
    attsOrder: store.cLOrderedStore.order.map(String),
    attsSelected: store.cLOrderedStore.checkListStore.selectedKeys.map(String),
    treeSlected: {
      treeData: store.attrsTreeStore.treeStdStore.treeData,
      selected: store.attrsTreeStore.selectedKeys.map(String),
      expanded: store.attrsTreeStore.expandedKeys.map(String),
    },
  });

export class EntityFiltersPageStore {
  constructor(props?: {
    objectId?: ZUid;
    selectionSettings?: SelectionSetings<ZEntityRow>;
    actions?: Set<EntFilterActionType>;
    actionsMeta?: EntFilterActionsMeta;
    defTSettingsStore?: DefaultTSettingsStore;
    initialFilters?: EntityFilters;
    filterRestrictions?: EntityFilterRestriction;
    /**
     * false, если restriction относится к конкретному экземпляру, а не к объекту:
     * такое состояние фильтров нельзя писать в user-preferences, они общие для объекта.
     */
    persistFilterState?: boolean;
    tabletemplateToken?: ATVTemplateToken;
    title?: string;
    save?: () => Promise<void>;
    onNewRow?: () => void;
    onStartCreate?: () => void;
    onLoad?: () => void;
    onRowClick?: (row: ZEntityRow, index?: number) => void;
  }) {
    const {
      objectId,
      onLoad,
      onRowClick,
      selectionSettings,
      actions,
      defTSettingsStore,
      initialFilters,
      filterRestrictions,
      persistFilterState,
      tabletemplateToken,
      actionsMeta,
      save,
      onNewRow,
      onStartCreate,
      title,
    } = props || {};
    if (selectionSettings) this.selectionSettings = selectionSettings;
    if (objectId) this.setCurrObjId(objectId);
    if (onLoad) this.onLoad = onLoad;
    if (onRowClick) this.onRowClick = onRowClick;
    if (onNewRow) this.onNewRow = onNewRow;
    if (onStartCreate) this.onStartCreate = onStartCreate;
    this.availableActions =
      actions ??
      new Set([
        EntFilterActionType.changeState,
        EntFilterActionType.copy,
        EntFilterActionType.create,
        EntFilterActionType.export,
        EntFilterActionType.import,
        EntFilterActionType.delete,
        EntFilterActionType.massEdit,
        EntFilterActionType.massDelete,
      ]);
    if (actionsMeta) this.actionsMeta = actionsMeta;
    if (defTSettingsStore) this.defTSettingsStore = defTSettingsStore;
    if (tabletemplateToken) this.tabletemplateToken = tabletemplateToken;
    this.stateConfig = new ConfigManager<ZEntityFilterStateConfig>(
      new EntityFilterStateStrategy(),
    );
    this.tableConfig = new ConfigManager<ZEntityTSettings>(
      new EntityTableStrategy(),
    );
    if (initialFilters) {
      this.initialFilters = initialFilters;
    }
    if (filterRestrictions) {
      this.filterRestrictions = filterRestrictions;
    }
    this.persistFilterState = persistFilterState ?? true;
    if (save) {
      this.save = save;
    }
    this.title = title;
    makeAutoObservable(this);
  }

  readonly title?: string;

  readonly persistFilterState: boolean;

  protected availableActions: Set<EntFilterActionType>;

  readonly actionsMeta: EntFilterActionsMeta | null = null;

  private selectionSettings: SelectionSetings<ZEntityRow> = {
    keepSelected: true,
    selectionType: "checkbox",
  };

  private onLoad: (() => void) | null = null;

  private onNewRow: (() => void) | null = null;

  private onStartCreate: (() => void) | null = null;

  defTSettingsStore: DefaultTSettingsStore | null = null;

  tabletemplateToken: ATVTemplateToken | null = null;

  stateConfig: ConfigManager<ZEntityFilterStateConfig>;

  tableConfig: ConfigManager<ZEntityTSettings>;

  onRowClick: ((row: ZEntityRow, index?: number) => void) | null = null;

  initialFilters: EntityFilters | null = null;

  setInitialFilters(value: EntityFilters | null) {
    this.initialFilters = value;
  }

  private save: (() => Promise<void>) | null = null;

  cLOrderedStore = new CheckListWithOrderStore();

  attrsTreeStore = new AttsTreeStore();

  attributesMap: Record<ZUid, ZAttribute> = {};

  setAttributesMap(map: Record<ZUid, ZAttribute>) {
    this.attributesMap = map;
  }

  get avalibleActionsSet() {
    return this.availableActions;
  }

  currObjId: ZUid | null = null;

  setCurrObjId(id: ZUid) {
    this.currObjId = id;
  }

  objectStates: ZObjState[] = [];

  setObjectStates(list: ZObjState[]) {
    this.objectStates = list;
  }

  currObjName: string = "";

  setCurrObjName(name: string) {
    this.currObjName = name;
  }

  curObject: ZObjectItem | null = null;

  setCurObject(obj: ZObjectItem | null) {
    this.curObject = obj;
  }

  loading = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  siderVisible: boolean = true;

  setSiderVisible(flag: boolean) {
    this.siderVisible = flag;
  }

  typesMap: Record<number, string> = {};

  setTypesMap(map: Record<number, string>) {
    this.typesMap = map;
  }

  tableStore: TableStore<ZEntityRow, EntityFilters> | null = null;

  setTableStore(store: TableStore<ZEntityRow, EntityFilters> | null) {
    this.tableStore = store;
  }

  filterRestrictions: EntityFilterRestriction | undefined = undefined;

  setFilterRestrictions(restrictions: EntityFilterRestriction | undefined) {
    const prev = this.filterRestrictions;
    const sameRestrictions =
      prev?.filterAttrId === restrictions?.filterAttrId &&
      prev?.filterValueStr === restrictions?.filterValueStr;
    if (sameRestrictions) {
      return;
    }
    this.filterRestrictions = restrictions;
  }

  get restrictedAttrId(): string | undefined {
    return this.filterRestrictions
      ? String(this.filterRestrictions.filterAttrId)
      : undefined;
  }

  get restrictedNodeKey(): string | undefined {
    return this.filterRestrictions
      ? makeFilterRestrictionNodeKey(this.filterRestrictions)
      : undefined;
  }

  isAttrRestriced(attrId: ZUid): boolean {
    return this.restrictedAttrId === String(attrId);
  }

  isNodeRestriced(node: TreeLikeData<EntityFilterTreeData>): boolean {
    return this.isNodeKeyRestricted(node.key);
  }

  isNodeKeyRestricted(key: React.Key): boolean {
    return this.restrictedNodeKey === String(key);
  }

  isNodeInRestrictedBranch(key: React.Key): boolean {
    const { restrictedNodeKey } = this;
    if (!restrictedNodeKey) return true;
    if (restrictedNodeKey === String(key)) return true;
    const path = this.attrsTreeStore.treeStdStore.findPath(key) ?? [];
    return path.map(String).includes(restrictedNodeKey);
  }

  isNodeDisabledByRestriction(key: React.Key): boolean {
    return !this.isNodeInRestrictedBranch(key);
  }

  applyTreeRestrictions() {
    const { treeStdStore } = this.attrsTreeStore;
    treeStdStore.walk((node) => {
      const disabled = this.isNodeDisabledByRestriction(node.key);
      if (node.disabled !== disabled) {
        treeStdStore.updateNode(node.key, { disabled });
      }
    });
  }

  ensureFilterRestrictions() {
    const { restrictedAttrId, restrictedNodeKey } = this;
    if (!restrictedAttrId || !restrictedNodeKey) return;

    const selectedAttrIds =
      this.cLOrderedStore.checkListStore.selectedKeys.map(String);
    this.cLOrderedStore.checkListStore.setSelectedKeysSet([
      restrictedAttrId,
      ...selectedAttrIds.filter((id) => id !== restrictedAttrId),
    ]);
    this.cLOrderedStore.setOrder([
      restrictedAttrId,
      ...this.cLOrderedStore.order
        .map(String)
        .filter((id) => id !== restrictedAttrId),
    ]);
  }

  keepOnlyFilterRestrictions() {
    const { restrictedAttrId, restrictedNodeKey } = this;
    if (!restrictedAttrId || !restrictedNodeKey) return;

    this.cLOrderedStore.checkListStore.setSelectedKeysSet([restrictedAttrId]);
    this.cLOrderedStore.setOrder([restrictedAttrId]);
    this.attrsTreeStore.setSelectedKeys([restrictedNodeKey]);
    this.attrsTreeStore.setExpandedKeys([]);
  }

  async resetFilters() {
    this.cLOrderedStore.reset();
    this.keepOnlyFilterRestrictions();
    await this.resetFilterTree({ stateIds: [] });
  }

  applyStateFilter(stateIds: ZUid[]) {
    const { tableStore } = this;
    if (!tableStore) return;
    const filters = tableStore.updateParams.filters ?? tableStore.filters;
    const next = Array.from(new Set(stateIds)).sort();
    const current = Array.from(new Set(filters?.stateIds ?? [])).sort();
    if (JSON.stringify(next) === JSON.stringify(current)) return;
    tableStore.setFilters({ ...filters, stateIds: next });
  }

  get canCreate(): boolean {
    const { curObject } = this;
    return (
      this.availableActions.has(EntFilterActionType.create) &&
      !!curObject &&
      hasPermissionIn(curObject, Permission.entityCreate)
    );
  }

  get canDelete(): boolean {
    const { curObject } = this;
    return (
      this.availableActions.has(EntFilterActionType.delete) &&
      !!curObject &&
      hasPermissionIn(curObject, Permission.entityDelete)
    );
  }

  get objectStatesOptions() {
    return this.objectStates.map((state) => ({
      label: state.name,
      value: state.id,
    }));
  }

  bsFuncs: BsFuncDef[] = [];

  setBsFuncs(funcs: BsFuncDef[]) {
    this.bsFuncs = funcs;
  }

  async init(
    objectId: ZUid,
    options?: {
      statesLoader?: () => Promise<ZObjState[]>;
    },
  ) {
    try {
      this.setLoading(true);
      this.setCurObject(null);
      this.cLOrderedStore.clear();
      this.attrsTreeStore.clear();
      this.setCurrObjId(objectId);
      const object = await loadObject(objectId, { translate: true });
      this.setCurrObjName(object.name);
      this.setCurObject(object);
      const { statesLoader } = options || {};
      this.setObjectStates(
        await (statesLoader || loadObjectStates)(objectId, { translate: true }),
      );
      this.setBsFuncs(await availableFunctions(objectId));
      this.setAttributesMap(
        makeObjectAttributesMap(
          await loadObjectAttrinbutesAll(String(objectId), { translate: true }),
        ),
      );
      this.setTypesMap(await loadAttrTypeMap());
      const key = userPreferenceKeys.enitiesSearchLP(objectId);
      const { data: savedParams } = await this.tableConfig.get(key);
      const isFirstView = !savedParams.filters?.objectId;

      await this.defTSettingsStore?.init(objectId);
      const initDefSettings = () =>
        this.defTSettingsStore?.initSettings(this.tableStore, isFirstView);

      const getSupportedOrderAttrs = async () => {
        const attrs = await loadFilterSupportedAtts(String(objectId));
        /**
         * Если атрибут выключен в настройках по умолчанию, то его нужно отфильтровать
         * пр этом, если this.defTSettingsStore?.visibleAttrs === undefined, то это
         * значит, что настройки не заданы и фильтровать не нужно
         */
        const needFilter = !!this.defTSettingsStore?.visibleAttrs;
        return needFilter
          ? attrs.filter((attr) =>
              this.defTSettingsStore?.visibleAttrs?.has(attr.id),
            )
          : attrs;
      };

      const supportedAtts = await getSupportedOrderAttrs();
      const attsOptions = supportedAtts.map(({ id }) => ({
        label: this.attributesMap[id]?.name || "",
        value: String(id),
      }));

      this.cLOrderedStore.checkListStore.setOptions(attsOptions);
      await this.loadEntitySearchState();

      const savedFilters = savedParams?.filters || {};
      const defFilters: EntityFilters = { objectId, ...this.initialFilters };
      const finalFilters = { ...savedFilters, ...defFilters };

      const finalParams: TableLoadParams<EntityFilters> = {
        ...(savedParams ?? this.defTSettingsStore?.sortParams),
        /* Сохранённые filters из LP подмешиваем с restriction из init (меню/URL), иначе таблица уйдёт без ограничения. */
        filters: withFilterRestriction(finalFilters, this.filterRestrictions),
      };

      const saveParams =
        this.save ??
        (async () => {
          const params = this.tableStore?.params;
          if (params)
            await this.tableConfig.save(key, getLatestEntityTSettings(params));
        });

      this.setTableStore(
        await compoundEntityTableStore2(
          objectId,
          "id",
          this.typesMap,
          makeObjectStatesMap(this.objectStates),
          finalParams,
          this.selectionSettings,
          async () => {
            await saveParams();
            this.onLoad?.();
            initDefSettings?.();
          },
          { visibleAttrs: this.defTSettingsStore?.visibleAttrs },
        ),
      );
    } catch (error) {
      onError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async appendFilterTree(node?: TreeLikeData<EntityFilterTreeData>) {
    try {
      if (!this.currObjId) {
        throw new Error("System error: currObjId = null");
      }
      const {
        attrsTreeStore: { treeStdStore },
        cLOrderedStore: { orderedSelected },
        typesMap,
      } = this;

      /* По текущему узлу находим шаг в orderedSelected: следующий атрибут — тот, чьи значения грузим. */
      const attrIndex = orderedSelected.findIndex(
        ({ value }) => value === String(node?.attrId),
      );
      const exist = attrIndex !== -1;
      const actualIndex = exist ? attrIndex : 0;
      const nextIndex = exist ? actualIndex + 1 : 0;
      const nextKey = orderedSelected[nextIndex]?.value;
      const isLeaf = nextIndex === orderedSelected.length - 1;
      const attribute = this.attributesMap[nextKey ?? ""];
      if (!attribute) return;
      this.attrsTreeStore.setLoading(true);
      const path = treeStdStore.findPath(node?.key || "") || [];
      const firstItem = { attrId: nextKey ?? "" };
      /* Цепочка фильтров для API: родительский путь + следующий attrId (без filterValue на последнем шаге UI). */
      const getParams = () => {
        if (node)
          return [
            firstItem,
            ...path
              .map((key) => treeStdStore.findNode(key))
              .map((item) => ({
                attrId: item?.attrId ?? "",
                filterValue: item?.filterValue,
              }))
              .filter((item) => !!item),
          ];
        return [firstItem];
      };
      const attrFilterItems: EntityFilterItem[] = getParams();
      const loadedValues = await loadTreeAttrValues(
        this.currObjId,
        attrFilterItems,
      );

      const selectedNodes = path
        ?.map((key) => treeStdStore.findNode(key))
        .filter((e) => !!e) as TreeLikeData<EntityFilterItem>[];
      const selectedVals = selectedNodes
        .map((e) => e.filterValue?.toSearch)
        .filter((e) => e && e.length > 0) as string[][];

      /* Ключ ноды должен совпадать с тем, что потом лежит в saved state / restriction (id + путь toSearch). */
      const treeDataProm = sortFilterTreeValues(
        attribute,
        loadedValues,
        typesMap,
      ).map((value) =>
        attr2TreeData(
          attribute,
          value,
          isLeaf,
          typesMap,
          [
            String(attribute.id),
            ...selectedVals.flat(),
            ...value.toSearch,
          ].join("-"),
        ),
      );

      if (node) {
        treeStdStore.appendNode(node.key, treeDataProm);
      } else {
        treeStdStore.setTreeData(treeDataProm);
      }
      this.applyTreeRestrictions();
      this.saveEntitySearchState();
    } catch (error) {
      onError(error);
    } finally {
      this.attrsTreeStore.setLoading(false);
    }
  }

  async resetFilterTree(filterOverrides?: Partial<EntityFilters>) {
    this.attrsTreeStore.clear();
    this.ensureFilterRestrictions();
    await this.appendFilterTree();
    if (this.restrictedNodeKey) {
      this.attrsTreeStore.setSelectedKeys([this.restrictedNodeKey]);
      this.attrsTreeStore.setExpandedKeys([]);
    }
    this.reloadWithActualFilters(filterOverrides);
    await this.saveEntitySearchState();
  }

  reloadWithActualFilters(filterOverrides?: Partial<EntityFilters>) {
    const {
      attrsTreeStore,
      attrsTreeStore: { treeStdStore },
    } = this;
    const nodes = (
      treeStdStore.findPath(attrsTreeStore.lastSelectedKey || "") || []
    )
      ?.map((key) => treeStdStore.findNode(key))
      .filter((e) => !!e) as TreeLikeData<EntityFilterItem>[];

    const lastNode = treeStdStore.findNode(
      attrsTreeStore.lastSelectedKey || "",
    );

    const hierarchicalFilter = {
      filters: nodes.map((n) =>
        /* Текущий (последний) узел в пути ещё без выбранного значения — в API уходит только attrId. */
        lastNode?.key === n.key
          ? { attrId: n.attrId }
          : { attrId: n.attrId, filterValue: n.filterValue },
      ),
      searchValue: lastNode?.filterValue?.toSearch || [],
    };

    const actualFilters = {
      ...(this.tableStore?.updateParams.filters ?? this.tableStore?.filters),
      hierarchicalFilter,
      ...filterOverrides,
    };
    this.tableStore?.setFilters(actualFilters, true);
  }

  async saveEntitySearchState() {
    try {
      /* Состояние сайдбара общее для объекта, поэтому не сохраняем «экземплярное» ограничение. */
      if (!this.persistFilterState) return;
      if (!this.currObjId) {
        throw new Error("System error: currObjId = null");
      }
      await this.stateConfig.save(
        userPreferenceKeys.enitiesSearchState(this.currObjId),
        store2EntitySearchState(this),
      );
    } catch (error) {
      onError(error);
    }
  }

  async loadEntitySearchState() {
    try {
      const state = await this.getSavedSearchState();
      if (!this.currObjId || !state) return;
      const { data: rawData } = state;
      /* Согласуем сохранённое дерево/чеклист с restriction до первого appendFilterTree (ключи и корень). */
      const data = withEntityFilterStateDataFilterRestriction(
        rawData,
        this.filterRestrictions,
      );
      const reconciledData = reconcileEntityFilterState(
        data,
        new Set(Object.keys(this.attributesMap)),
      );
      const {
        cLOrderedStore,
        cLOrderedStore: { checkListStore },
        attrsTreeStore,
      } = this;
      checkListStore.setSelectedKeysSet(reconciledData.attsSelected);
      cLOrderedStore.setOrder(reconciledData.attsOrder);
      attrsTreeStore.setSelectedKeys(reconciledData.treeSlected.selected);
      attrsTreeStore.setExpandedKeys(reconciledData.treeSlected.expanded);
      /* чтобы дерево отобразилось кооректно
       * необходимо заново загрузить его структуру
       */
      await this.appendFilterTree();

      const treeLoadersList: (() => Promise<void>)[] = [];
      walk((node) => {
        const loadNodes = async () => {
          /**
           * нам не нужно грузить дерево полностью
           * загружаем только те ноды, которые были уже созданы
           */
          if (node.children && node.children.length > 0)
            await this.appendFilterTree(node);
        };
        treeLoadersList.push(loadNodes);
      }, reconciledData.treeSlected.treeData);

      /**
       * Загрузить дерево нужно последовательно, чтобы ключи соответствовали
       * тем настройкам, которые сохранил пользователь, если использовать Promise.All,
       * то получим нестабильный результат
       */
      const promiseOnion = treeLoadersList.reduce(
        (acc, curr) => acc.then(curr),
        Promise.resolve(),
      );
      await promiseOnion;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  async getSavedSearchState(): Promise<ZEntityFilterStateConfig | null> {
    try {
      if (!this.currObjId) {
        throw new Error("System error: currObjId = null");
      }
      return this.stateConfig.get(
        userPreferenceKeys.enitiesSearchState(this.currObjId),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  }

  tableIsInited: boolean = false;

  setTableIsInited(flag: boolean) {
    this.tableIsInited = flag;
  }

  subCardStore: EntityCardStore | null = null;

  setSubCardStore(store: EntityCardStore | null) {
    this.subCardStore = store;
  }

  openCreation() {
    try {
      const { currObjId } = this;
      if (!currObjId) {
        return;
      }
      const store = new EntityCardStore();
      this.setSubCardStore(store);
      store.initNew(currObjId);
      this.onStartCreate?.();
    } catch (error) {
      onError(error);
    }
  }

  closeCreation() {
    this.setSubCardStore(null);
  }

  handleNewRow() {
    this.tableStore?.reload();
    this.setSubCardStore(null);
    this.onNewRow?.();
  }
}
